import type { AccountTeamMember } from "../../../../Store/Reducers/AccountDetailsSlice";
import { EMAIL_REGEX } from "../../../../Utils/Constants/RegexConstants";
import {
  ACCOUNT_TEAM_ROLE_OPTIONS,
  ACCOUNT_TEAM_ROLES,
} from "../../../../Utils/Constants/RoleConstants";

/** A person in the org directory who may be added to an account team. */
export type AccountTeamCandidate = {
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  position: string;
  role_name: string;
  /** True if this person is already on the account team (shown but not selectable). */
  isAlreadyAdded?: boolean;
};

/** The membership fields sent for one person. */
export type AccountMemberDetails = {
  role: string;
  position: string;
  phone: string;
  languages: string;
};

export type AccountTeamMemberPayload = {
  email: string;
  full_name: string;
  phone: string;
  position: string;
  role: string;
  grade: number;
  languages: string;
};

/** Grade sent for in-app adds; matches the pre-existing single-member form. */
export const DEFAULT_ADD_GRADE = 1;

export const ACCOUNT_ROLE_FILTER_ALL = "all";

export const ACCOUNT_ROLE_FILTER_OPTIONS = [
  { label: "All roles", value: ACCOUNT_ROLE_FILTER_ALL },
  ...ACCOUNT_TEAM_ROLE_OPTIONS,
];

export const normalizeEmailKey = (value: string | null | undefined) =>
  (value || "").trim().toLowerCase();

/**
 * Map a directory role name onto a selectable account-team role. Unknown roles
 * fall back to AE rather than producing an option the Select cannot display.
 */
export const candidateRoleValue = (roleName: string | null | undefined) => {
  const code = (roleName || "").trim().toUpperCase();
  return ACCOUNT_TEAM_ROLE_OPTIONS.some(option => option.value === code)
    ? code
    : ACCOUNT_TEAM_ROLES.AE;
};

export const emptyDetails = (): AccountMemberDetails => ({
  role: ACCOUNT_TEAM_ROLES.AE,
  position: "",
  phone: "",
  languages: "",
});

/**
 * A directory member's details come straight from their organization record —
 * there is no editing step, so this is what gets submitted.
 * `organization_users` has no languages column, hence the blank.
 */
export const detailsFromCandidate = (
  candidate: AccountTeamCandidate
): AccountMemberDetails => ({
  role: candidateRoleValue(candidate.role_name),
  position: candidate.position || "",
  phone: candidate.phone || "",
  languages: "",
});

/** Index current members by lowercased email for duplicate detection. */
export const indexMembersByEmail = (members: AccountTeamMember[]) => {
  const map = new Map<string, AccountTeamMember>();
  members.forEach(member => {
    const key = normalizeEmailKey(member.email);
    if (key) map.set(key, member);
  });
  return map;
};

/**
 * Mark candidates who are already on the account team. These are shown in the
 * picker but cannot be selected (displayed with a disabled style).
 */
export const markExistingMembers = (
  candidates: AccountTeamCandidate[],
  existingByEmail: Map<string, AccountTeamMember>
): AccountTeamCandidate[] =>
  candidates.map(candidate => ({
    ...candidate,
    isAlreadyAdded: existingByEmail.has(normalizeEmailKey(candidate.email)),
  }));

/**
 * @deprecated Use `markExistingMembers` instead to show already-added members
 * with a disabled style rather than hiding them entirely.
 */
export const excludeExistingMembers = (
  candidates: AccountTeamCandidate[],
  existingByEmail: Map<string, AccountTeamMember>
) =>
  candidates.filter(
    candidate => !existingByEmail.has(normalizeEmailKey(candidate.email))
  );

export const filterCandidates = (
  candidates: AccountTeamCandidate[],
  search: string,
  roleFilter: string
) => {
  const text = (search || "").trim().toLowerCase();
  return candidates.filter(candidate => {
    const roleMatch =
      roleFilter === ACCOUNT_ROLE_FILTER_ALL ||
      candidateRoleValue(candidate.role_name) === roleFilter;
    const searchMatch =
      !text ||
      candidate.full_name.toLowerCase().includes(text) ||
      candidate.email.toLowerCase().includes(text);
    return roleMatch && searchMatch;
  });
};

/**
 * Which kind of add is being made. A submission is *either* one existing
 * directory user or one brand-new person, never both: approving a known org
 * user is cheap, while approving a new one creates an organization_users row,
 * and mixing the two makes the approver judge both at the pace of the riskier.
 */
export type AddMode = "directory" | "new";

/** The one brand-new person being introduced to the org. */
export type NewMember = {
  name: string;
  email: string;
  details: AccountMemberDetails;
};

export const emptyNewMember = (): NewMember => ({
  name: "",
  email: "",
  details: emptyDetails(),
});

export type NewMemberIssue =
  | { kind: "none" }
  | { kind: "incomplete" }
  | { kind: "invalid-email" }
  | { kind: "already-on-team"; member: AccountTeamMember }
  | { kind: "in-directory"; candidate: AccountTeamCandidate };

export const isNewMemberTouched = (member: NewMember) =>
  Boolean(
    member.name.trim() || member.email.trim() || member.details.position.trim()
  );

/**
 * Validate the new-member form. An address that already belongs to a directory
 * user is reported as `in-directory` rather than accepted — creating a second
 * record would collide on the unique email, and the caller offers a route back
 * to the list instead.
 */
export const validateNewMember = (
  member: NewMember,
  {
    candidates,
    existingByEmail,
  }: {
    candidates: AccountTeamCandidate[];
    existingByEmail: Map<string, AccountTeamMember>;
  }
): NewMemberIssue => {
  const email = member.email.trim();
  const key = normalizeEmailKey(email);

  if (email && !EMAIL_REGEX.test(email)) return { kind: "invalid-email" };

  if (key) {
    const onTeam = existingByEmail.get(key);
    if (onTeam) return { kind: "already-on-team", member: onTeam };

    const inDirectory = candidates.find(
      candidate => normalizeEmailKey(candidate.email) === key
    );
    if (inDirectory) return { kind: "in-directory", candidate: inDirectory };
  }

  if (!member.name.trim() || !member.details.position.trim() || !email) {
    return { kind: "incomplete" };
  }

  return { kind: "none" };
};

export const buildDirectoryMember = (
  candidate: AccountTeamCandidate
): AccountTeamMemberPayload => {
  const details = detailsFromCandidate(candidate);
  return {
    email: candidate.email.trim(),
    full_name: candidate.full_name.trim(),
    phone: details.phone.trim(),
    position: details.position.trim(),
    role: details.role,
    grade: DEFAULT_ADD_GRADE,
    languages: details.languages.trim(),
  };
};

export const buildNewMember = (
  member: NewMember
): AccountTeamMemberPayload => ({
  email: member.email.trim(),
  full_name: member.name.trim(),
  phone: member.details.phone.trim(),
  position: member.details.position.trim(),
  role: member.details.role,
  grade: DEFAULT_ADD_GRADE,
  languages: member.details.languages.trim(),
});

/**
 * The single place a submission payload is produced. Taking the mode as an
 * argument is what makes a mixed batch unrepresentable rather than merely
 * unreachable through the current UI. Always one member, never more.
 */
export const buildSubmission = (
  mode: AddMode,
  {
    selectedCandidate,
    newMember,
  }: {
    selectedCandidate: AccountTeamCandidate | null;
    newMember: NewMember;
  }
): AccountTeamMemberPayload[] => {
  if (mode === "directory") {
    return selectedCandidate ? [buildDirectoryMember(selectedCandidate)] : [];
  }
  return [buildNewMember(newMember)];
};

/** Batch names are mode-specific so approvers can triage at a glance. */
export const defaultBatchName = (mode: AddMode) =>
  mode === "directory"
    ? "In-app account team add"
    : "In-app account team add — new member";
